import { useState } from "react";
import { CheckCircle2, ClipboardCheck, XCircle } from "lucide-react";
import api from "@/lib/api";

// Option 3b gate: from Day 2 the participant confirms the daily disciplines before the video opens.
export const CheckinGate = ({ state, auth, onDone, endpoint = "/classroom/checkin", soft = false }) => {
  const qs = state.questions || [];
  const [ans, setAns] = useState(() => Object.fromEntries(qs.map((q) => [q.id, null])));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const blocked = !soft && state.status === "checkin_blocked";
  const allAnswered = qs.every((q) => ans[q.id] !== null);
  const submit = async () => {
    if (soft) { onDone({ ok: true, all_yes: true }); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(endpoint, { ...auth(), answers: Object.fromEntries(qs.map((q) => [q.id, !!ans[q.id]])) });
      onDone(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not submit. Please try again."); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6" data-testid="classroom-checkin">
      <div className="rounded-2xl border border-border bg-white p-5 sm:p-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-accent">Day {state.day} · before today's class</p>
        <h2 className="mt-1 font-serif text-2xl sm:text-3xl flex items-center gap-2"><ClipboardCheck size={24} className="text-accent" /> Daily commitment check</h2>
        <p className="mt-2 text-sm text-muted-foreground">{soft ? <>Please reflect honestly on the mandatory guidelines agreed for this program. Answer each question, then open today's class.</> : <>Please confirm honestly that you are following all the mandatory guidelines agreed for this program. Today's video opens only when every answer is <strong>Yes</strong>.</>}</p>
        {blocked && (
          <div data-testid="checkin-blocked" className="mt-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
            <XCircle size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div><p className="font-semibold">Please complete the pending tasks and join again.</p><p className="text-xs mt-0.5">Your last answers (submitted {new Date(state.checkin.at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" })} IST) had a No for: {qs.filter((q) => !state.checkin.answers?.[q.id]).map((q) => q.label).join(", ")}. Once done, answer again below.</p></div>
          </div>
        )}
        <ol className="mt-5 space-y-3">
          {qs.map((q, i) => (
            <li key={q.id} data-testid={`checkin-q-${q.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-4 py-3">
              <span className="text-sm font-medium"><span className="text-muted-foreground mr-2">{i + 1}.</span>{q.label}</span>
              <span className="inline-flex rounded-full border border-border p-0.5 bg-muted/40">
                {[["no", "No", false], ["yes", "Yes", true]].map(([k, l, v]) => (
                  <button key={k} type="button" data-testid={`checkin-${q.id}-${k}`} onClick={() => setAns((a) => ({ ...a, [q.id]: v }))} className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${ans[q.id] === v ? (v ? "bg-emerald-600 text-white" : "bg-red-600 text-white") : "text-muted-foreground hover:bg-white"}`}>{l}</button>
                ))}
              </span>
            </li>
          ))}
        </ol>
        {err && <p className="mt-3 text-sm text-red-600" data-testid="checkin-error">{err}</p>}
        <button type="button" data-testid="checkin-submit" disabled={!allAnswered || busy} onClick={submit} className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold disabled:opacity-50">
          <CheckCircle2 size={16} /> {busy ? "Submitting…" : "Submit and open today's class"}
        </button>
        <p className="mt-2 text-[11px] text-muted-foreground">{soft ? "These answers are for your own reflection and are not recorded. Your daily tasks are tracked in the Daily Mandatory Tasks table." : "Your answers are recorded for the organiser's daily report."}</p>
      </div>
    </div>
  );
};
